import { Link, useLocation } from "react-router-dom";
import { useState } from "react";
import { Menu, X, Phone, Mail } from "lucide-react";
import { useContentText } from "../lib/content";

const NAV_LINKS = [
  { to: "/business-name-protection", label: "Benefits" },
  { to: "/how-it-works", label: "How it works" },
  { to: "/pricing", label: "Pricing" },
  { to: "/insights", label: "Blogs & Guidance" },
  {
    to: "/about", label: "About",
    submenu: [
      { to: "/about#you",     label: "About you",         desc: "Who Business Name Protection is built for" },
      { to: "/about#product", label: "About the product", desc: "What you get, how it works, and what it costs" },
      { to: "/about#nbr",     label: "About NBR",         desc: "The UK group that runs the service" },
    ],
  },
  { to: "/faqs", label: "FAQs" },
  { to: "/partnerships", label: "Partners" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  const phone = useContentText("footer.contact.phone", "0800 069 9090");
  const phoneDigits = phone.replace(/\D/g, "");
  const email = useContentText("footer.contact.email", "info@nbrg.co.uk");

  return (
    <header className="sticky top-0 z-40 backdrop-blur-md bg-[#f7f7f7]/85 border-b border-[#9ac7f7]/25" data-testid="site-navbar">
      {/* NBR Group signature mint help banner - sitewide, all viewports */}
      <div className="bg-[#71edc9] text-[#04273d]" data-testid="help-banner">
        <div className="px-5 sm:px-8 lg:px-12 xl:px-16 2xl:px-24 py-2 flex items-center justify-center gap-x-2.5 gap-y-0.5 flex-wrap text-center">
          <Phone size={13} className="shrink-0" />
          <span className="font-mono-brand text-[10px] sm:text-[11px] tracking-[0.14em] uppercase font-semibold">
            We're here to help - speak to a UK protection expert
          </span>
          <a href={`tel:${phoneDigits}`} className="font-bold text-xs sm:text-sm tracking-wide underline-offset-2 hover:underline" data-testid="help-banner-call">
            {phone}
          </a>
          <a href={`mailto:${email}`} className="hidden sm:inline-flex items-center gap-1 font-semibold text-xs tracking-wide underline-offset-2 hover:underline" data-testid="help-banner-email">
            <Mail size={12} /> {email}
          </a>
        </div>
      </div>

      <div className="px-5 sm:px-8 lg:px-12 xl:px-8 2xl:px-20 py-3 2xl:py-4 min-[1700px]:py-5 flex items-center justify-between gap-4 xl:gap-6 min-[1700px]:gap-8">
        <div className="flex items-center min-w-0 shrink-0">
          <a
            href="https://nationalbusinessregister.co.uk"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2.5 min-[1700px]:gap-3 group"
            data-testid="nav-logo-link"
            aria-label="Visit the NBR Group website (opens in a new tab)"
          >
            <img
              src={`${process.env.REACT_APP_BACKEND_URL || ""}/api/static/brand/logo.png`}
              alt="NBR Business Name Protection"
              className="h-11 sm:h-12 lg:h-12 2xl:h-14 min-[1700px]:h-16 w-auto shrink-0"
              data-testid="nav-logo-image"
            />
            {/* Brand text - hidden on mid-range widths (1024-1700px) where
                the nav row is already comfortably full. Shown on mobile (sm-md)
                and on very wide ≥1700px desktops only. */}
            <div className="leading-tight hidden sm:block xl:hidden">
              <p className="font-display font-bold text-[#04273d] text-sm min-[1700px]:text-lg tracking-tight whitespace-nowrap">Business Name Protection</p>
              <p className="font-mono-brand text-[9px] min-[1700px]:text-xs text-[#447694] tracking-widest uppercase whitespace-nowrap">A NBR Group Service</p>
            </div>
          </a>
        </div>

        <nav className="hidden xl:flex items-center gap-1 2xl:gap-1.5 min-[1700px]:gap-2">
          {NAV_LINKS.map((l) => {
            const active = loc.pathname === l.to || (l.submenu && loc.pathname.startsWith(l.to));
            if (l.submenu) {
              return (
                <div key={l.to} className="relative group" data-testid={`nav-${l.label.toLowerCase()}-dropdown-wrapper`}>
                  <Link
                    to={l.to}
                    data-testid={`nav-${l.label.toLowerCase()}-link`}
                    className={`px-3 py-2 rounded-full text-sm font-medium transition-colors flex items-center gap-1 whitespace-nowrap ${
                      active ? "bg-[#04273d] text-white" : "text-[#04273d] hover:bg-[#9ac7f7]/20"
                    }`}
                  >
                    {l.label}
                    <svg className="w-3 h-3 min-[1700px]:w-3.5 min-[1700px]:h-3.5 mt-px transition-transform group-hover:rotate-180" viewBox="0 0 12 12" fill="currentColor"><path d="M6 8L2 4h8L6 8z"/></svg>
                  </Link>
                  <div
                    className="absolute left-0 top-full pt-2 w-72 min-[1700px]:w-80 invisible opacity-0 translate-y-1 group-hover:visible group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-150 z-50"
                    data-testid={`nav-${l.label.toLowerCase()}-dropdown`}
                  >
                    <div className="bg-white border border-[#9ac7f7]/30 rounded-2xl shadow-xl p-2">
                      {l.submenu.map((s) => (
                        <Link
                          key={s.to}
                          to={s.to}
                          className="block px-4 py-3 rounded-xl hover:bg-[#9ac7f7]/15 transition-colors"
                          data-testid={`nav-${l.label.toLowerCase()}-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
                        >
                          <p className="font-display font-semibold text-[#04273d] text-sm min-[1700px]:text-base">{s.label}</p>
                          <p className="text-xs min-[1700px]:text-sm text-[#447694] mt-0.5">{s.desc}</p>
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              );
            }
            return (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}-link`}
                className={`px-3 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                  active ? "bg-[#04273d] text-white" : "text-[#04273d] hover:bg-[#9ac7f7]/20"
                }`}
              >
                {l.label}
              </Link>
            );
          })}
        </nav>

        <div className="hidden xl:flex items-center gap-2 2xl:gap-2.5 min-[1700px]:gap-3 shrink-0">
          <Link to="/" className="text-sm text-[#447694] hover:text-[#0d9b80] font-display font-semibold transition-colors whitespace-nowrap" data-testid="nav-home-link">Home</Link>
          <Link to="/account" className="text-sm text-[#447694] hover:text-[#0d9b80] font-display font-semibold transition-colors whitespace-nowrap mr-1" data-testid="nav-account-link">My account</Link>
          <Link to="/check" className="btn-secondary text-sm py-2.5 px-5 whitespace-nowrap" data-testid="nav-check-name-button">Check a name</Link>
          <Link to="/pricing" className="btn-primary text-sm py-2.5 px-5 whitespace-nowrap" data-testid="nav-get-protected-button">Get protected</Link>
        </div>

        <button
          className="xl:hidden p-2 text-[#04273d]"
          onClick={() => setOpen((o) => !o)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {open && (
        <div className="xl:hidden bg-white border-t border-[#9ac7f7]/25 px-5 py-4 flex flex-col gap-1" data-testid="nav-mobile-menu">
          <Link
            to="/"
            onClick={() => setOpen(false)}
            data-testid="nav-mobile-home-link"
            className="px-3 py-2.5 rounded-xl text-[#04273d] font-medium hover:bg-[#9ac7f7]/15 block"
          >
            Home
          </Link>
          {NAV_LINKS.map((l) => (
            <div key={l.to}>
              <Link
                to={l.to}
                onClick={() => setOpen(false)}
                data-testid={`nav-mobile-${l.label.toLowerCase().replace(/\s+/g, "-")}-link`}
                className="px-3 py-2.5 rounded-xl text-[#04273d] font-medium hover:bg-[#9ac7f7]/15 block"
              >
                {l.label}
              </Link>
              {l.submenu && (
                <div className="ml-5 mb-1 flex flex-col gap-0.5">
                  {l.submenu.map((s) => (
                    <Link
                      key={s.to}
                      to={s.to}
                      onClick={() => setOpen(false)}
                      data-testid={`nav-mobile-${l.label.toLowerCase()}-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className="px-3 py-2 rounded-xl text-[#447694] text-sm hover:bg-[#9ac7f7]/10"
                    >
                      › {s.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
          <div className="flex gap-2 mt-2">
            <Link to="/check" onClick={() => setOpen(false)} className="btn-secondary text-sm flex-1 justify-center">Check a name</Link>
            <Link to="/pricing" onClick={() => setOpen(false)} className="btn-primary text-sm flex-1 justify-center">Get protected</Link>
          </div>
          <Link to="/account" onClick={() => setOpen(false)} className="px-3 py-2.5 rounded-xl text-[#04273d] text-sm font-medium hover:bg-[#9ac7f7]/15 block mt-1 text-center border border-[#9ac7f7]/30" data-testid="nav-mobile-account-link">
            My account
          </Link>
        </div>
      )}
    </header>
  );
}
